import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTenant } from '../../contexts/TenantContext';
import { useErrorToast } from '../common';

export const ProtectedRoute = ({ 
  children, 
  requiredPermission = null,
  requiredRole = null,
  requireJLLUser = false
}) => {
  const { authState, authService } = useOktaAuth();
  const {
    loading: tenantLoading,
    initialized: tenantInitialized,
    hasPermission,
    hasRole,
    isJLLUser,
    hasNoTenantAccess,
  } = useTenant();
  const { showErrorToast } = useErrorToast();

  const roles = requiredRole ? (Array.isArray(requiredRole) ? requiredRole : [requiredRole]) : null;
  const permissions = requiredPermission
    ? (Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission])
    : null;
  const hasRequiredRole = roles ? roles.some(role => hasRole(role)) : true;
  const hasRequiredPermission = permissions ? permissions.some(permission => hasPermission(permission)) : true;
  let accessDeniedMessage = null;

  if (!tenantLoading && tenantInitialized && authState?.isAuthenticated) {
    if (requireJLLUser && !isJLLUser()) {
      accessDeniedMessage = 'Access denied. This page is only accessible to JLL administrators.';
    } else if (roles && !hasRequiredRole) {
      accessDeniedMessage = `Access denied. Required role: ${roles.join(' or ')}.`;
    } else if (permissions && !hasRequiredPermission) {
      accessDeniedMessage = `Access denied. Required permission: ${permissions.join(' or ')}.`;
    }
  }

  useEffect(() => {
    if (accessDeniedMessage) {
      showErrorToast(accessDeniedMessage);
    }
  }, [accessDeniedMessage, showErrorToast]);

  if (!authState || tenantLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  if (authState.error) {
    console.error('Authentication error:', authState.error);
    return <Navigate to="/signin" replace />;
  }

  if (!authState.isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  if (!tenantLoading && hasNoTenantAccess && hasNoTenantAccess()) {
    const AccessDeniedPage = React.lazy(() => import('../../pages/AccessDenied/AccessDeniedPage'));
    return (
      <React.Suspense fallback={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6">Loading...</Typography>
        </Box>
      }>
        <AccessDeniedPage />
      </React.Suspense>
    );
  }

  if (accessDeniedMessage) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
